import { Link } from "@tanstack/react-router";
import type { DashboardWorkspace } from "@/api/types";
import { cn } from "@/lib/cn";
import { dashboardSectionPath, useDashboardActiveSlug } from "@/lib/dashboard-context";
import { DashboardWorkspaceSwitcher } from "./DashboardWorkspaceSwitcher";
import { dashboardNavItemsForRole } from "./dashboard-nav";

interface DashboardBottomNavProps {
  role?: string;
  businessName?: string;
  workspaces?: DashboardWorkspace[];
  switchingWorkspace?: boolean;
  onWorkspaceSelect?: (workspace: DashboardWorkspace) => void;
  onListNewBusiness?: () => void;
}

function DashboardBottomNav({
  role,
  businessName = "Your Business",
  workspaces = [],
  switchingWorkspace = false,
  onWorkspaceSelect,
  onListNewBusiness,
}: DashboardBottomNavProps) {
  const tabs = dashboardNavItemsForRole(role);
  const currentSlug = useDashboardActiveSlug();

  return (
    <nav className="dashboard-bottom-nav fixed inset-x-0 bottom-0 z-50 border-t border-pro-border bg-pro-bg pb-[env(safe-area-inset-bottom)] md:hidden">
      <div className="flex h-14 items-center justify-around">
        {tabs.map((tab) => {
          const to = currentSlug ? dashboardSectionPath(currentSlug, tab.section) : tab.to;

          return (
            <Link key={tab.to} to={to} preload="render" className="flex flex-1 justify-center">
              {({ isActive }) => (
                <span
                  className={cn(
                    "mx-2 flex w-full flex-col items-center gap-0.5 rounded-md py-1 transition-colors duration-micro",
                    isActive
                      ? "bottom-nav-active bottom-nav-pill text-lime"
                      : "text-pro-text-faint",
                  )}
                >
                  <tab.icon className="size-5" />
                  <span className="text-[10px] font-medium">{tab.label}</span>
                </span>
              )}
            </Link>
          );
        })}
        <div className="flex flex-1 justify-center">
          <DashboardWorkspaceSwitcher
            businessName={businessName}
            workspaces={workspaces}
            bottomNav
            switching={switchingWorkspace}
            onSelect={(workspace) => onWorkspaceSelect?.(workspace)}
            onListNewBusiness={() => onListNewBusiness?.()}
          />
        </div>
      </div>
    </nav>
  );
}

export { DashboardBottomNav };
